import { Layout } from '@/Layout';
import { AiSuggestFailureLabel } from '@/components/AiSuggestSummaryBanner/AiSuggestFailureLabel';
import { AiSuggestSuccessLabel } from '@/components/AiSuggestSummaryBanner/AiSuggestSuccessLabel';
import { ReferencedReportLink } from '@/components/AiSuggestSummaryBanner/ReferencedReportLink';
import { useSelectedApPayee } from '@/components/ApPayee/useChangeApPayee';
import { EDocMetadataProps } from '@/components/EDocMetadata';
import { useLoginInfo } from '@/components/LoginInfo/provider';
import {
  ApReportFormInputOfficeMemberPositionDeptsSelect,
  ApReportFormInputTextField,
} from '@/components/ReportForm';
import { useGetBookDateByPayeeType } from '@/components/ReportForm/ApReportFormInputInvoiceBookDateField/hooks';
import { useIsActiveInstantPayee } from '@/components/ReportForm/InstantPayeeSwitcher/hooks/useIsActiveInstantPayee';
import { ReportFormStatusContainer } from '@/components/ReportForm/ReportFormStatusContainer';
import { useTpmInvoiceLoading } from '@/context/TpmInvoiceLoading';
import { useGetEDocPlan } from '@/context/services/e_doc_plan/EDocPlan.service';
import { useGetExcisesAllSuspense } from '@/context/services/excise/Excise.service';
import { INVOICE_NUMBER_UNSAVED } from '@/context/services/reportsType/invoiceReports/type';
import { useConvertApiDataToPayeeFormName } from '@/context/services/reportsType/invoiceReports/useConvertApiDataAndPayeeForm';
import { useConvertApiDataToReportForm } from '@/context/services/reportsType/invoiceReports/useConvertApiDataAndReportForm';
import { useConvertApiDataToSpecialExceptionName } from '@/context/services/reportsType/invoiceReports/useConvertApiDataAndSpecialExceptionForm';
import { useFormInputIdMapping } from '@/context/services/reportsType/invoiceReports/useFormInputIdMapping';
import {
  LoadingPdfViewer,
  PdfViewer,
} from '@/features/InvoiceReport/Components/PdfViewer/PdfViewer';
import {
  useInvoiceFileValue,
  useInvoiceReportsRoute,
  useTransactionProperties,
} from '@/features/InvoiceReport/Edit/components/Context/API';
import { useIsLoadingWithAllApi } from '@/features/InvoiceReport/Edit/components/Context/ComponentAPI';
import { OcrProvider } from '@/features/InvoiceReport/Edit/components/Context/OcrContext';
import { useVirtualItem } from '@/features/InvoiceReport/Edit/components/Context/VirtualItems';
import { FlashMessages } from '@/features/InvoiceReport/Edit/components/FlashMessages';
import { useValidationRules } from '@/features/InvoiceReport/Edit/components/InvoiceTransaction/Columns/hooks';
import { useFloorOnAbsolute } from '@/features/InvoiceReport/Edit/components/InvoiceTransaction/hooks';
import {
  useCheckEvent,
  useCheckEventForInvoiceTransaction,
} from '@/features/InvoiceReport/Edit/components/InvoiceTransaction/hooks/useCheckEvent';
import { useDetailsSize } from '@/features/InvoiceReport/Edit/components/InvoiceTransaction/hooks/useDetailsSize';
import { useValidateMinusTotalValue } from '@/features/InvoiceReport/Edit/components/InvoiceTransaction/hooks/useValidateMinusTotalValue';
import { ConvertInvoiceTransaction } from '@/features/InvoiceReport/Edit/components/InvoiceTransaction/model';
import { WrapReportsForm } from '@/features/InvoiceReport/Edit/components/Loadings';
import { OcrDevToolProps } from '@/features/InvoiceReport/Edit/components/OcrDevTool';
import { OcrInferenceComponent } from '@/features/InvoiceReport/Edit/components/OcrInference/OcrInferenceComponent';
import { ReportFormInputs } from '@/features/InvoiceReport/Edit/components/ReportFormInputs/ReportFormInputs';
import { WrapInlineNotification } from '@/features/InvoiceReport/Edit/components/WrapInlineNotification';
import {
  useFilteredIsHiddenReportFormInputs,
  useSubmitPaymentReport,
} from '@/features/InvoiceReport/Edit/components/hooks/hooks';
import { useFillPaymentRequestDetails } from '@/features/InvoiceReport/Edit/components/hooks/useFillPaymentRequestDetails';
import { useFillPaymentRequestSummary } from '@/features/InvoiceReport/Edit/components/hooks/useFillPaymentRequestSummary';
import {
  useFindInvoiceFileId,
  useFindInvoicePayeeId,
  useFindInvoiceRegistrationNumber,
  useHasInvoicePayee,
} from '@/features/InvoiceReport/Edit/components/hooks/useFindReportFormInputTypes';
import { useGetInvoiceReportPath } from '@/features/InvoiceReport/Edit/components/hooks/useGetInvoiceReportPath';
import { useHasWithholdingIncomeTax } from '@/features/InvoiceReport/Edit/components/hooks/useHasWithholdingIncomeTax';
import { useOcrFileHandler } from '@/features/InvoiceReport/Edit/components/hooks/useOcrFileHandler';
import {
  ApiErrors,
  ErrorFields,
} from '@/features/InvoiceReport/Edit/components/type';
import { mapApiCustomTransactionItemsToFormValues } from '@/features/InvoiceReport/Edit/components/utils/customTransactionItems';
import {
  isAiSuggestFailed,
  isAiSuggestSuccessWithoutReference,
} from '@/features/invoices/utils/aiSuggestStatus';
import { useFlexibleValidationFlag } from '@/hooks/feature_flag/useFlexibleValidationFlag';
import { usePaymentRequestSupportAgentFlag } from '@/hooks/feature_flag/usePaymentRequestSupportAgentFlag';
import {
  typeGardeApReportStatus,
  useReportStatusLabel,
} from '@/hooks/invoice_reports/useReportStatusLabel';
import {
  useDocumentPictureInPicture,
  useSupportDocumentPictureInPicture,
} from '@/hooks/useDocumentPictureInPicture';
import { useWarningTrackingSystem } from '@/hooks/useErrorTrackingSystem';
import { useStateRef } from '@/hooks/useStateRef';
import { useTranslation } from '@/i18n';
import yup from '@/libs/validation';
import { isEditableReportStatus } from '@/utils/ApReportStatus';
import { isWarn, parseErrorSchema, traverseErrors } from '@/utils/errors';
import { useGlobalContainerRef } from '@/wc/helper/ref';
import {
  ButtonGroup,
  ButtonV2,
  Form,
  IconInfo,
  Loading,
  TextLink,
  useBoundingClientRect,
  useWindowResize,
} from '@moneyforward/ap-frontend-components';
import { useBooleanFlagValue } from '@openfeature/react-sdk';
import {
  ApReportStatus,
  InvoiceReportDetailFormInputsResponse,
} from 'ap-openapi';
import Big from 'big.js';
import classnames from 'classnames/bind';
import { stringToDayjs } from 'date-util';
import {
  CSSProperties,
  FC,
  LazyExoticComponent,
  Suspense,
  lazy,
  memo,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  FieldError,
  FieldErrors,
  FieldPath,
  FormProvider,
  useFieldArray,
  useForm,
} from 'react-hook-form';
import { ValidationError } from 'yup';
import { InvoiceTransaction, PaymentRequestForm } from '../type.d';
import { useDetailValue, useFormValue } from './Context';
import { Details } from './Details';
import { mappingTypeValue, valueOrValues } from './form_value_mappings';
import styles from './index.module.scss';
const DevTool = lazy(() =>
  import('@hookform/devtools').then(({ DevTool }) => ({
    default: process.env.NODE_ENV === 'development' ? DevTool : () => <></>,
  }))
);

let OcrDevTool: undefined | LazyExoticComponent<FC<OcrDevToolProps>>;
if (process.env.NODE_ENV === 'development') {
  OcrDevTool = lazy(() =>
    //@ts-ignore
    import('./OcrDevTool/OcrDevTool.tsx').then(({ OcrDevTool }) => ({
      default: OcrDevTool,
    }))
  );
}
const cx = classnames.bind(styles);

type Props = {
  invoiceReportsId: string;
};

const InnerInvoiceReportEdit: FC<Props> = memo(({}) => {
  const isValidateMaximumInvoiceTransactionCountFlag = useBooleanFlagValue(
    'validate-maximum-invoice-transaction-count',
    false
  );
  const isSupportGlobalRequiredSetting = useFlexibleValidationFlag();
  const useProjectSelectById = useBooleanFlagValue(
    'use-project-select-for-report-form',
    false
  );
  const { currentOffice } = useLoginInfo();
  const { apReceiptTypeAlertType } = useGetEDocPlan();
  const { t } = useTranslation();
  const floorOnAbsolute = useFloorOnAbsolute();
  const route = useInvoiceReportsRoute();
  const apiData = useFormValue();
  const detailData = useDetailValue();
  const isSupportAgentEnabled = usePaymentRequestSupportAgentFlag();
  const displayAiSuggestFailureLabel = isAiSuggestFailed(
    apiData.invoice_ai_suggestion?.status
  );
  const referencedReport = apiData.invoice_ai_suggestion?.referenced_report;
  const referenceNumber = referencedReport?.number;
  const displayAiSuggestSuccessLabel = isAiSuggestSuccessWithoutReference(
    apiData.invoice_ai_suggestion
  );
  const aiSuggestLabel = useMemo(() => {
    if (!isSupportAgentEnabled) return undefined;
    if (displayAiSuggestFailureLabel) return <AiSuggestFailureLabel />;
    if (displayAiSuggestSuccessLabel) return <AiSuggestSuccessLabel />;
    return undefined;
  }, [
    isSupportAgentEnabled,
    displayAiSuggestFailureLabel,
    displayAiSuggestSuccessLabel,
  ]);
  const convertApiDataToReportForm = useConvertApiDataToReportForm();
  const reportFormInputFormNames: Record<
    string,
    string | string[] | undefined
  > = useMemo(() => {
    return (
      convertApiDataToReportForm(apiData?.report_form_inputs ?? []).reduce(
        (prev, cur) => {
          let value = (
            mappingTypeValue[cur.type] ||
            ((cur) =>
              valueOrValues(cur.input_values?.map((item) => item.value)) ??
              cur.default_value ??
              undefined)
          )(cur);
          /**
           * NOTE: 空文字列の場合選択系コンポーネントで空文字で選択されるので明示的に `undefined` をセットする様にする
           * If it is an empty string, it will be selected with an empty string in the selection component, so explicitly set it to `undefined`.
           */
          value = typeof value === 'string' && value === '' ? undefined : value;
          const options = cur.input_options?.map((item) => item.value) ?? [];
          const formValue = (Array.isArray(value) ? value : [value]).filter(
            Boolean
          );
          const isTextFormType = [
            'ApReportFormInputTextField',
            'ApReportFormInputTextArea',
          ].includes(cur.type);

          const isOtherValue =
            (cur.has_other_value &&
              !isTextFormType &&
              formValue.length > 0 &&
              formValue.some((value) => options.every((v) => v !== value))) ||
            false;
          const hasOtherInput =
            cur.has_other_value && !isTextFormType
              ? {
                  [`${cur.id}_other`]: isOtherValue ? value : undefined,
                  [`${cur.id}_is_other_checked`]: isOtherValue,
                }
              : {};

          return {
            [cur.id]: isOtherValue ? undefined : value,
            ...hasOtherInput,
            ...prev,
          };
        },
        {}
      ) ?? {}
    );
  }, [apiData?.report_form_inputs, convertApiDataToReportForm]);
  const payeeForm = useConvertApiDataToPayeeFormName(
    useFilteredIsHiddenReportFormInputs(apiData.report_form_inputs ?? []),
    {
      is_instant_payee_enabled: apiData.is_instant_payee_enabled,
      status: apiData.status,
    }
  );
  const specialExceptionForm = useConvertApiDataToSpecialExceptionName(
    useFilteredIsHiddenReportFormInputs(apiData.report_form_inputs ?? [])
  );

  const transactionProperties = useTransactionProperties();

  const invoiceFileValue = useInvoiceFileValue();
  const apiInvoiceFile = useMemo(() => {
    if (apiData.invoice_file && invoiceFileValue) {
      const file = new File([invoiceFileValue], apiData.invoice_file.name!);
      return {
        file,
        mfFileId: apiData.invoice_file.id!,
        invoiceId: '',
      };
    }
    return undefined;
  }, [apiData.invoice_file, invoiceFileValue]);
  const invoiceTransactions = useMemo(
    () =>
      detailData.map((item, idx): InvoiceTransaction => {
        const taxIncludedTotal = Big(item?.total_value || 0).plus(
          item?.excise_value || 0
        );
        const quantity = Big(item?.quantity || 1);

        const transactionItems = mapApiCustomTransactionItemsToFormValues(
          item.custom_transaction_items,
          transactionProperties
        );

        return ConvertInvoiceTransaction({
          id: item.id!,
          number: item.number ?? INVOICE_NUMBER_UNSAVED,
          dealDate: stringToDayjs(item.deal_date),
          name: item.name,
          exItemId: item.ex_item?.id,
          exItemName: item.ex_item?.name,
          unit: item.unit_value,
          quantity: item.quantity,
          totalValue: item.total_value,
          drExciseId: item.dr_excise?.id,
          drExciseName: item.dr_excise?.name,
          invoiceKind: item.invoice_kind ?? '', // FIXME: whether it should get getInvoiceKind from detailData condition or not.
          isInvoiceKindDetectionTarget:
            route === 'normal' && apiData.status === ApReportStatus.unsubmitted,
          exciseValue: item.excise_value,
          hasWithholdingIncomeTax: item.has_withholding_income_tax,
          withholdingIncomeTax: item.withholding_income_tax_value,
          memo: item.memo,
          deptId: item.dept?.id,
          deptName: item.dept?.name,
          ...(useProjectSelectById
            ? {
                projectId: item.project?.id,
                projectName: item.project?.name,
              }
            : {
                projectCodeId: item.project_code?.id,
                projectCodeName: item.project_code?.name,
              }),
          currency: item.currency ?? 'JPY',
          useCustomJPYRate: item.use_custom_jpy_rate,
          jpyRate: item.jpyrate ?? 1,
          crItemId: item.cr_item?.id,
          crItemName: item.cr_item?.name,
          crSubItemId: item.cr_sub_item?.id,
          crSubItemName: item.cr_sub_item?.name,
          // JPY: To prevent discrepancies caused by decimals in consumption tax calculation,
          // truncate values toward zero (same behavior as Old UI).
          taxIncludedTotalValue:
            item.currency === 'JPY'
              ? floorOnAbsolute(taxIncludedTotal.toNumber())
              : taxIncludedTotal.toNumber(),
          taxIncludedUnit:
            item.currency === 'JPY'
              ? floorOnAbsolute(taxIncludedTotal.div(quantity).toNumber())
              : taxIncludedTotal.div(quantity).toNumber(),
          rowKey: `invoice-transaction_${idx}`,
          ...(transactionItems &&
            Object.keys(transactionItems).length > 0 && {
              custom_transaction_items: transactionItems,
            }),
        });
      }),
    [
      detailData,
      floorOnAbsolute,
      transactionProperties,
      route,
      apiData.status,
      useProjectSelectById,
    ]
  );
  // https://github.com/moneyforward/ex_web/blob/e9f2cd07e7382546ce17141208cc120dd39e5413/client/components/other/invoice_reports/edit.tsx#L170
  const canDisplayDrExcise = useMemo(() => {
    const isExistsNonZeroExcise = invoiceTransactions.some(
      (item) => item.exciseValue !== 0
    );
    return isExistsNonZeroExcise || !apiData.duty_free;
  }, [apiData.duty_free, invoiceTransactions]);

  const defaultValues = useMemo(
    () => ({
      id: apiData.id,
      title: apiData.title,
      report_dept: apiData.report_dept?.is_changeable
        ? apiData.report_dept?.initial_selected_id
        : '',
      invoice_file: apiInvoiceFile,
      payee: payeeForm,
      specialException: specialExceptionForm,
      reportForm: reportFormInputFormNames,
      invoiceTransactions: invoiceTransactions,
      deleteInvoiceTransactions: [],
    }),
    [
      apiData.id,
      apiData.title,
      apiData.report_dept?.is_changeable,
      apiData.report_dept?.initial_selected_id,
      apiInvoiceFile,
      invoiceTransactions,
      payeeForm,
      reportFormInputFormNames,
      specialExceptionForm,
    ]
  );

  const methods = useForm<PaymentRequestForm>({
    defaultValues: defaultValues,
    mode: 'all',
  });
  const { control, setFocus, watch, setError, getValues } = methods;
  const { fields, append, replace, remove, insert } = useFieldArray<
    PaymentRequestForm,
    'invoiceTransactions'
  >({
    control: control,
    name: 'invoiceTransactions',
  });
  const { append: addDeleteRecord } = useFieldArray<
    PaymentRequestForm,
    'deleteInvoiceTransactions'
  >({
    control: control,
    name: 'deleteInvoiceTransactions',
  });

  const [draftInstantPayeeBankName, setDraftInstantPayeeBankName] =
    useState<string>();
  const [draftInstantPayeeBankBranchName, setDraftInstantPayeeBankBranchName] =
    useState<string>();

  const watchedInvoiceTransactions = watch('invoiceTransactions');
  const resetInvoiceTransactionsField = () => {
    watchedInvoiceTransactions.forEach((transaction) => {
      if (transaction.number !== INVOICE_NUMBER_UNSAVED) {
        addDeleteRecord({ id: transaction.id });
      }
    });
    methods.resetField('invoiceTransactions', { defaultValue: [] });
  };

  const hasTransactionsInForm = useMemo(() => {
    return !!watchedInvoiceTransactions.length;
  }, [watchedInvoiceTransactions]);

  const fillPaymentRequestSummary = useFillPaymentRequestSummary({
    setValue: methods.setValue,
    clearErrors: methods.clearErrors,
  });
  const fillPaymentRequestDetails = useFillPaymentRequestDetails({
    replace,
    resetInvoiceTransactionsField: resetInvoiceTransactionsField,
  });

  const getInvoiceTransactionIndex = useCallback((name: string) => {
    // invoiceTransactions.index.xxx or invoiceTransactions[index].xxx
    const regexp = /.*(?:\[(?<index1>[0-9].*)\]|\.(?<index2>[0-9].*))\..*/gm;
    const { index1, index2 } = regexp.exec(name)?.groups ?? {};
    try {
      const index = index1 || index2;
      if (index) {
        return parseInt(index);
      }
      return undefined;
    } catch (error) {
      return undefined;
    }
  }, []);
  const { virtualParentRef, rowVirtualizer, handleInvalidScroll } =
    useVirtualItem({ fields, getIndex: getInvoiceTransactionIndex });

  const {
    id: invoiceRegistrationNumberId,
    isHidden: isHiddenInvoiceRegistrationNumber,
  } = useFindInvoiceRegistrationNumber(apiData.report_form_inputs ?? []);
  const selectedPayee = useSelectedApPayee();
  const { checked: includeTax, onChange: onChangeIncludeTax } = useCheckEvent(
    selectedPayee?.is_tax_included
  );
  const { checked: showColumnDetails, onChange: onChangeShowColumnDetails } =
    useCheckEventForInvoiceTransaction(invoiceTransactions, false);
  const isActiveInstantPayee = useIsActiveInstantPayee({
    control,
    name: 'payee.value',
  });
  const isEnabledInstantPayee = useMemo(() => {
    return (
      apiData.is_instant_payee_enabled ||
      apiData.status !== ApReportStatus.unsubmitted
    );
  }, [apiData.status, apiData.is_instant_payee_enabled]);
  const hasWithholdingIncomeTax =
    useHasWithholdingIncomeTax(invoiceTransactions);
  const rules = useValidationRules(
    includeTax,
    apiData.permit_minus_transaction,
    showColumnDetails,
    hasWithholdingIncomeTax,
    isActiveInstantPayee,
    isSupportGlobalRequiredSetting
  );
  const { yupSchema } = rules;
  const schema: yup.ObjectSchema<{
    invoiceTransactions: Partial<InvoiceTransaction>[];
  }> = useMemo(
    () =>
      yup.object().shape({
        invoiceTransactions: yup.array().of(yupSchema).default([]),
      }),
    [yupSchema]
  );

  const container = useRef<HTMLDivElement | null>(null);
  const [errors, setErrors] = useState<ApiErrors>({
    invoiceReport: undefined,
    invoiceTransactions: undefined,
    invoiceTransactionsAlert: undefined,
    invoiceTransactionsRow: undefined,
  });

  const { show } = useGetInvoiceReportPath();

  const resetError = useCallback((key: keyof ApiErrors) => {
    setErrors((prev) => ({
      ...prev,
      [key]: undefined,
    }));
  }, []);
  const { send: sendWarning } = useWarningTrackingSystem();
  const handleInlineNotificationErrors = useCallback(
    (
      errFields: ErrorFields | FieldErrors<PaymentRequestForm> | ApiErrors
    ): boolean => {
      const typeGuardApiErrors = (
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        errFields: any
      ): errFields is ApiErrors => {
        return [
          'invoiceReport',
          'invoiceTransactions',
          'invoiceTransactionsAlert',
          'invoiceTransactionsRow',
        ].every((name) => Object.keys(errFields).includes(name));
      };
      const typeGuardErrorField = (value: unknown): value is FieldError => {
        if (
          typeof value === 'object' &&
          value !== null &&
          'type' in value &&
          'message' in value
        ) {
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
          const typed = value as Record<string, unknown>;
          return typeof typed.message === 'string';
        }
        return false;
      };
      if (typeGuardApiErrors(errFields)) {
        setErrors(errFields);
        return !errFields.invoiceReport || !errFields.invoiceTransactions;
      } else {
        const errMsgs: string[] = [];
        const alertMsgs: string[] = [];
        let unknownError: boolean = false;
        (function parseErrorFields(
          fields: ErrorFields | FieldErrors<PaymentRequestForm> | ApiErrors
        ) {
          Object.entries(fields).forEach(([, value]) => {
            if (!value) {
              unknownError = true;
              return;
            }
            if (typeGuardErrorField(value)) {
              if (value.message) {
                if (isWarn(value.type)) {
                  alertMsgs.push(value.message);
                } else {
                  errMsgs.push(value.message);
                }
              }
            } else {
              parseErrorFields(value);
            }
          });
        })(errFields);
        if (unknownError) {
          sendWarning(JSON.stringify(errFields));
        }
        setErrors({
          invoiceTransactions: Array.from(new Set(errMsgs)).join('<br />'),
          invoiceTransactionsAlert: Array.from(new Set(alertMsgs)).join(
            '<br />'
          ),
          invoiceReport: undefined,
          invoiceTransactionsRow: undefined,
        });
        return errMsgs.length > 0;
      }
    },
    [sendWarning]
  );
  const onInvalid = useCallback(
    async (errors: FieldErrors<PaymentRequestForm>) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let err: Record<string, any> = errors;
      try {
        const invoiceTransactions = getValues('invoiceTransactions');
        await schema.validate(
          {
            invoiceTransactions,
          },
          {
            abortEarly: false,
          }
        );
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (e: any) {
        if (!(e instanceof ValidationError)) {
          throw e;
        }
        const errFields = parseErrorSchema(e, true);
        err = {
          ...err,
          ...errFields,
        };
        Object.keys(errFields).forEach((key) => {
          const field = key.split('.').slice(-1)[0];
          if (field && field in err) {
            delete err[field];
          }
        });
      }
      handleInlineNotificationErrors(err);
      const errFields = traverseErrors(errors);
      if (errFields.length > 0 && container.current) {
        const nameControls = container.current.querySelectorAll('[name]');
        const firstErrorControl = Array.from(nameControls).find((item) =>
          errFields.includes(item.getAttribute('name') ?? '')
        );
        if (firstErrorControl) {
          firstErrorControl.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
          const name = (firstErrorControl.getAttribute('name') ??
            '') as FieldPath<PaymentRequestForm>;
          setFocus(name);
        } else {
          handleInvalidScroll(container.current, errFields[0] ?? '');
        }
      }
    },
    [
      getValues,
      handleInlineNotificationErrors,
      handleInvalidScroll,
      schema,
      setFocus,
    ]
  );

  const { zoom, zoomUpdate, minimum, minimumUpdate, Icon } = useDetailsSize();
  const isPiPSupport = useSupportDocumentPictureInPicture();
  const isDraggable = !isPiPSupport && zoom;
  const { innerHeight } = useWindowResize();
  const detailHeight = !minimum ? innerHeight * 0.36 : 92; // 36%
  const [, setMainContainerRef, wrapMainContainerRef] =
    useStateRef<HTMLDivElement | null>();
  const size = useBoundingClientRect(wrapMainContainerRef);
  const { top: mainTop } = size;
  const mainContainerHeight = innerHeight - (detailHeight + mainTop);
  const mainContainerStyle: CSSProperties = useMemo(
    () => ({ maxHeight: mainContainerHeight, minHeight: mainContainerHeight }),
    [mainContainerHeight]
  );
  const detailContainerStyle: CSSProperties = useMemo(
    () => ({
      minHeight: detailHeight,
      maxHeight: detailHeight,
    }),
    [detailHeight]
  );
  const pdfViewerAreaRef = useRef<HTMLDivElement | null>(null);
  const pdfViewerContainerRef = useRef<HTMLDivElement | null>(null);
  const { openPictureInPicture: onPictureInPicture } =
    useDocumentPictureInPicture(pdfViewerContainerRef);
  const isLoading = useIsLoadingWithAllApi();
  const reportStatusLabel = useReportStatusLabel();
  const file = watch('invoice_file');
  const invoiceFile = useMemo(() => {
    if (file) {
      if (file instanceof File) {
        return file;
      }
      return file.file;
    }
    return undefined;
  }, [file]);

  const { handleFileChange: handleOcrFileChange } = useOcrFileHandler();
  const onChangeFile = useCallback(
    (file: File) => {
      handleOcrFileChange(file);
    },
    [handleOcrFileChange]
  );

  const onWrapPictureInPicture = useCallback(
    async (value: boolean) => {
      if (invoiceFile) {
        await onPictureInPicture(value);
      }
    },
    [invoiceFile, onPictureInPicture]
  );
  const formInputIdMap = useFormInputIdMapping(
    useFilteredIsHiddenReportFormInputs(apiData.report_form_inputs ?? [])
  );
  const invoicePayeeTypeInfo:
    | InvoiceReportDetailFormInputsResponse
    | undefined = useFindInvoicePayeeId(
    useFilteredIsHiddenReportFormInputs(apiData.report_form_inputs ?? [])
  );
  const invoiceFileTypeId = useFindInvoiceFileId(
    useFilteredIsHiddenReportFormInputs(apiData.report_form_inputs ?? [])
  );
  const bookDate = useGetBookDateByPayeeType({
    control,
    originalBookDate: payeeForm.bookDate,
  });
  const exciseList = useGetExcisesAllSuspense();
  const isHaveSetApPayeeIdByDefault = useHasInvoicePayee(
    useFilteredIsHiddenReportFormInputs(apiData.report_form_inputs ?? [])
  );

  const validateMinusTotalValue = useValidateMinusTotalValue(
    handleInlineNotificationErrors,
    methods.getValues('invoiceTransactions'),
    apiData.permit_minus_transaction,
    exciseList
  );

  const bookDateInputId = watch('payee.reportForm.bookDate.reportFormInputId');
  const dueDateInputId = watch('payee.reportForm.dueDate.reportFormInputId');
  const payeeInputId = watch('payee.reportForm.payee.reportFormInputId');

  const {
    onSubmit,
    onDeleteSubmit,
    DeleteForm,
    ConfirmModal,
    isLoading: isSubmitLoading,
  } = useSubmitPaymentReport(
    schema,
    formInputIdMap,
    invoicePayeeTypeInfo,
    invoiceFileTypeId,
    setError,
    handleInlineNotificationErrors,
    handleInvalidScroll,
    validateMinusTotalValue,
    isHaveSetApPayeeIdByDefault,
    isValidateMaximumInvoiceTransactionCountFlag,
    transactionProperties
  );
  const scanDecision = useMemo(
    () =>
      !apiData.is_e_doc_ap_receipt_type_enabled && apReceiptTypeAlertType
        ? undefined
        : apiData.invoice_file?.e_doc_meta_datum
            ?.is_satisfy_depth_requirements &&
          apiData.invoice_file?.e_doc_meta_datum
            ?.is_satisfy_resolution_requirement
        ? 'ok'
        : apReceiptTypeAlertType,
    [
      apReceiptTypeAlertType,
      apiData.invoice_file?.e_doc_meta_datum?.is_satisfy_depth_requirements,
      apiData.invoice_file?.e_doc_meta_datum?.is_satisfy_resolution_requirement,
      apiData.is_e_doc_ap_receipt_type_enabled,
    ]
  );
  const eDocMetadata: EDocMetadataProps | undefined = useMemo(() => {
    if (apiData?.invoice_file?.e_doc_meta_datum) {
      const value = apiData.invoice_file.e_doc_meta_datum;
      return {
        dpi: value.dpi_text ?? '',
        pixelNumber: value.pixel_number_text ?? '',
        imageDepth: value.rgb_depth_text ?? '',
        type:
          scanDecision === 'warning' || scanDecision === 'error'
            ? scanDecision
            : undefined,
        isScan: value.is_scan ?? false,
        isSatisfyResolutionRequirement:
          value.is_satisfy_resolution_requirement ?? false,
        isSatisfyDepthRequirements:
          value.is_satisfy_depth_requirements ?? false,
      };
    }
    return undefined;
  }, [apiData?.invoice_file?.e_doc_meta_datum, scanDecision]);
  const containerRef = useGlobalContainerRef();

  const SubmitButtonGroup: FC<{
    status: ApReportStatus | undefined;
    onDeleteSubmit: () => void;
  }> = memo(function SubmitButtonGroup({ status, onDeleteSubmit }) {
    const route = useInvoiceReportsRoute();
    return (
      <ButtonGroup>
        {isEditableReportStatus(status) ? (
          <>
            <ButtonV2
              isTransparent
              color='danger'
              size='sm'
              onClick={onDeleteSubmit}
            >
              {t('payment_request_edit_btn_delete')}
            </ButtonV2>
            <ButtonV2
              isSecondary
              color='primary'
              size='sm'
              type='submit'
              formAction='draft'
            >
              {t('payment_request_edit_btn_tmpsubmit')}
            </ButtonV2>
            <ButtonV2 color='primary' size='sm' type='submit' formAction='next'>
              {t('payment_request_edit_btn_submit')}
            </ButtonV2>
          </>
        ) : (
          <ButtonV2
            isSecondary
            color='primary'
            size='sm'
            type='submit'
            formAction={route === 'approving' ? 'savedByApproving' : 'draft'}
          >
            {t('payment_request_edit_btn_draft')}
          </ButtonV2>
        )}
      </ButtonGroup>
    );
  });

  return (
    <>
      {/* z-indexの調整をしているのでモーダルにして先頭になる様にする / I'm adjusting the z-index so that it will be modal and appear at the top. */}
      <LoadingComponent
        containerRef={containerRef}
        isSubmitting={isSubmitLoading}
      />
      <FormProvider {...methods}>
        <OcrInferenceComponent
          invoiceRegistrationNumberKey={invoiceRegistrationNumberId}
          bookDateInputId={bookDateInputId}
          dueDateInputId={dueDateInputId}
          payeeInputId={payeeInputId}
          isHiddenInvoiceRegistrationNumber={isHiddenInvoiceRegistrationNumber}
          isActiveInstantPayee={isActiveInstantPayee}
          canDisplayDrExcise={canDisplayDrExcise}
          willOverwriteTransactions={hasTransactionsInForm}
          isEnabledInstantPayee={isEnabledInstantPayee}
          draftInstantPayeeBankName={draftInstantPayeeBankName}
          draftInstantPayeeBankBranchName={draftInstantPayeeBankBranchName}
          fillPaymentRequestSummary={fillPaymentRequestSummary}
          fillPaymentRequestDetails={fillPaymentRequestDetails}
          control={control}
          isDutyFree={apiData.duty_free}
        />
        <Form<PaymentRequestForm>
          formMethod={methods}
          onSubmit={onSubmit}
          onInvalid={onInvalid}
          className={cx(styles['parent'])}
          action='?'
        >
          <Layout
            key={`申請番号:${apiData.number ?? ''}_${isLoading}`}
            title='申請番号:'
            reportStatus={
              typeGardeApReportStatus(apiData.status)
                ? reportStatusLabel(apiData.status)
                : ''
            }
            titleLink={
              <TextLink href={show} target='_blank'>
                {apiData.number ?? ''}
              </TextLink>
            }
            titleExtra={aiSuggestLabel}
            headerRight={
              <SubmitButtonGroup
                status={
                  typeGardeApReportStatus(apiData.status)
                    ? apiData.status
                    : undefined
                }
                onDeleteSubmit={onDeleteSubmit}
              />
            }
            showHeaderBorderBottom
            hideContentPadding
            isLoading={isLoading}
          >
            <div className={cx(styles['container'])} ref={container}>
              <div
                ref={setMainContainerRef}
                className={cx(styles['main-container'])}
                style={mainContainerStyle}
              >
                <div
                  className={cx(styles['report-form-container'], {
                    [styles['hidden']]: zoom,
                  })}
                >
                  <WrapReportsForm>
                    <div className={cx(styles['report-form-container'])}>
                      <WrapInlineNotification
                        type='error'
                        isVisible={!!errors.invoiceReport}
                        message={errors.invoiceReport ?? ''}
                      />
                      <FlashMessages flashMessages={apiData?.flash_messages} />
                      <div
                        className={cx(styles['information-container'], {
                          [styles['with-ai-suggestion']]:
                            isSupportAgentEnabled && !!referenceNumber,
                        })}
                      >
                        {isSupportAgentEnabled && referenceNumber && (
                          <ReferencedReportLink
                            number={referenceNumber}
                            detailPath={referencedReport?.detail_path}
                          />
                        )}
                        <ReportFormStatusContainer
                          invoiceId={apiData.received_invoice?.invoice_number}
                          officeMember={apiData.office_member?.display_name}
                          agentApplicantOfficeMember={
                            apiData.proxy_office_member?.display_name
                          }
                        />
                        {apiData.is_skip_approval && (
                          <div
                            className={cx(styles['is-skip-approval-container'])}
                          >
                            <IconInfo size={14} />
                            <span>
                              {t(
                                'payment_request_edit_is_skip_approval_message'
                              )}
                            </span>
                          </div>
                        )}
                      </div>
                      <div className={cx(styles['input-container'])}>
                        <ApReportFormInputTextField<PaymentRequestForm>
                          name='title'
                          control={methods.control}
                          inputId='title-id'
                          label='申請タイトル'
                          required
                        />
                        {apiData.report_dept && (
                          <ApReportFormInputOfficeMemberPositionDeptsSelect<PaymentRequestForm>
                            name='report_dept'
                            control={methods.control}
                            inputId='office-member-position-depts-id'
                            label='所属部門'
                            required={false}
                            officeMemberId={apiData.office_member?.id || ''}
                            reportDept={apiData.report_dept}
                          />
                        )}
                        {apiData.report_form_inputs?.map((item) => {
                          return (
                            <ReportFormInputs
                              key={`${item.id}_${isLoading}`}
                              item={item}
                              control={methods.control}
                              isInstantPayeeEnabled={isEnabledInstantPayee}
                              reportFormInputs={
                                apiData.report_form_inputs ?? []
                              }
                              isDutyFree={apiData.duty_free}
                              createdByOfficeMemberId={
                                apiData.office_member?.id
                              }
                              setDraftInstantPayeeBankName={
                                setDraftInstantPayeeBankName
                              }
                              setDraftInstantPayeeBankBranchName={
                                setDraftInstantPayeeBankBranchName
                              }
                            />
                          );
                        })}
                      </div>
                    </div>
                  </WrapReportsForm>
                </div>
                {isLoading ? (
                  <LoadingPdfViewer />
                ) : (
                  <div
                    className={cx(styles['pdf-viewer-container'], {
                      [styles['draggable-pdf-viewer-container']]:
                        isDraggable && zoom,
                    })}
                    ref={pdfViewerContainerRef}
                  >
                    <PdfViewer
                      key={invoiceFile?.name}
                      title={invoiceFile?.name ?? ''}
                      onChangeFile={onChangeFile}
                      draggable={isDraggable}
                      bounds={pdfViewerAreaRef.current!}
                      file={invoiceFile}
                      timestamp={
                        currentOffice?.eDoc.isEnabled &&
                        apiData.invoice_file?.e_doc_meta_datum !== undefined &&
                        apiData?.invoice_file?.e_doc_meta_datum !== null
                      }
                      scanner={scanDecision}
                      eDocMeta={eDocMetadata}
                      zoom={zoom}
                      invoice_file_name={apiData?.invoice_file?.name}
                    />
                  </div>
                )}
              </div>
              <div
                className={cx(styles['detail-container'])}
                style={detailContainerStyle}
              >
                <Details
                  control={control}
                  fields={fields}
                  append={append}
                  remove={remove}
                  replace={replace}
                  insert={insert}
                  isDetailMinimum={minimum}
                  setIsDetailMinimum={minimumUpdate}
                  isDetailZoom={zoom}
                  setIsDetailZoom={zoomUpdate}
                  WindowIcon={Icon}
                  viewerContainerRef={pdfViewerAreaRef}
                  onZoom={onWrapPictureInPicture}
                  bookDate={bookDate}
                  isPermitMinusTransaction={apiData.permit_minus_transaction}
                  includeTax={includeTax}
                  onChangeIncludeTax={onChangeIncludeTax}
                  showColumnDetails={showColumnDetails}
                  onChangeShowColumnDetails={onChangeShowColumnDetails}
                  canDisplayDrExcise={canDisplayDrExcise}
                  invoiceTransactionErrors={errors.invoiceTransactions}
                  invoiceTransactionAlert={errors.invoiceTransactionsAlert}
                  isDutyFree={apiData.duty_free}
                  virtualContainerRef={virtualParentRef}
                  rowVirtualizer={rowVirtualizer}
                  invoiceTransactionRowErrors={errors.invoiceTransactionsRow}
                  resetError={resetError}
                  exciseList={exciseList}
                />
              </div>
            </div>
          </Layout>
        </Form>
      </FormProvider>
      <DeleteForm />
      <ConfirmModal />
      <Suspense fallback={<></>}>
        {/*@ts-ignore */}
        <DevTool control={control} />
        {OcrDevTool && (
          <OcrDevTool
            fillPaymentRequestSummary={fillPaymentRequestSummary}
            fillPaymentRequestDetails={fillPaymentRequestDetails}
          />
        )}
      </Suspense>
    </>
  );
});
InnerInvoiceReportEdit.displayName = 'InnerInvoiceReportEdit';

export const InvoiceReportEdit: FC<Props> = memo((props) => {
  return (
    <OcrProvider>
      <InnerInvoiceReportEdit {...props} />
    </OcrProvider>
  );
});
InvoiceReportEdit.displayName = 'InvoiceReportEdit';

type LoadingComponentProps = {
  isSubmitting: boolean;
  containerRef: () => HTMLElement | ShadowRoot;
};

const LoadingComponent: FC<LoadingComponentProps> = memo(
  ({ isSubmitting, containerRef }) => {
    const { isTpmFetching, setTpmFetching } = useTpmInvoiceLoading();
    const onLoadingClose = useCallback(() => {
      setTpmFetching(false);
    }, [setTpmFetching]);

    return (
      <Loading
        getContainer={containerRef}
        open={isSubmitting || isTpmFetching}
        isDialogMode
        size='xlarge'
        color='white'
        onClose={onLoadingClose}
      />
    );
  }
);
LoadingComponent.displayName = 'LoadingComponent';
